import { contentOf as coreContentOf, isReadOk as coreIsReadOk, isNeedLogin as coreIsNeedLogin, hasUnavailableMarker as coreHasUnavailableMarker, failMessage as coreFailMessage } from '@/utils/responseGuard'

export function safeObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

export function safeArray(value) {
  return Array.isArray(value) ? value : []
}

export function safeString(value, fallback = '') {
  if (value === null || value === undefined) return fallback
  return String(value)
}

export function safeNumber(value, fallback = 0) {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

// ── 响应 guard:Step6 收敛到 responseGuard 核 ───────────────────────────────
// 原本文件自带 4 套 token 函数(isUnavailableToken/isBlockingReadToken/isHardBlockingToken/isSoftBlockingToken)
// 及 envelope/marker 机器已全删,统一走核。这些是读家族用法(布局/bonus/平台列表),一律 fail-open。
export function safeContent(response) {
  return coreContentOf(response)
}
export function responseContent(response) {
  return coreContentOf(response)
}
export function isOkResponse(response) {
  return coreIsReadOk(response)
}
export function isReadableOkResponse(response) {
  return coreIsReadOk(response)
}
export function isNeedLogin(response) {
  return coreIsNeedLogin(response)
}
export function hasUnavailableMarker(response) {
  return coreHasUnavailableMarker(response)
}
export function responseFailMessage(response, fallback = '') {
  return coreFailMessage(response, fallback || 'This page is temporarily unavailable. Please try again later.')
}

export function normalizePlatformList(response) {
  const content = safeContent(response)
  return safeArray(
    content.list ||
      content.dataList ||
      content.platformList ||
      content.records ||
      content.items
  )
}

export function safeJsonParse(value, fallback = null) {
  if (!value) return fallback
  if (typeof value === 'object') return value
  if (typeof value !== 'string') return fallback

  try {
    return JSON.parse(value)
  } catch (e) {
    return fallback
  }
}

export function getTodayString() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ---- C1/C3: host 白名单(log-only 观测层,第一步不拦截)----
// 主干硬编码 apex(后缀匹配,覆盖 pay./wg./api. 等子域)。来源:service/valoran-demacia/src/main/filters/otbet.properties + 生产 home_page_banner 快照。
// 注意:这些是后端配置样本,线上真实收银台 redirect 域待生产快照校准。log-only:命中非白名单只上报不拦截。

// ★★★★★ 品牌自有域名 —— 换域名只改这一处 ★★★★★
// vukavi.com / vukavi.bet = VUKAVI 主品牌域(改版新域,已加入白名单)。
// ot.game / otgame.bet / 599win.bet = 旧域,生产 home_page_banner 实测真域,
//   **生产切换到 vukavi 域前必须保留**(删除会让线上 banner/支付/外链校验拒真域→功能受损);
//   待生产完全切到 vukavi 域后,可安全删除旧域(本数组一行即可)。
// 后续换新域名: ① 直接改下面数组; 或 ② 上线不发版,配环境变量 VUE_APP_BRAND_DOMAINS=newdomain.com (多个用逗号/空格分隔)。
// 后缀匹配会自动覆盖其所有子域(pay./www./m. 等); 下方 PAYMENT/EXTERNAL 两个白名单都会自动带上品牌域,无需两处改。
export const BRAND_HOST_DOMAINS = parseHostList([
  'vukavi.com', 'vukavi.bet', 'vukavi.net', 'ot.game', 'otgame.bet', '599win.bet'
], 'VUE_APP_BRAND_DOMAINS')

export const PAYMENT_HOST_ALLOWLIST = parseHostList([
  ...BRAND_HOST_DOMAINS,
  'otpay.io', 'paystack.co', 'palmpay-inc.com',
  'paga.com', 'try2best.africa', 'qg-pay.com', 'gtrpay001.com', 'voocorp.com',
  'ipayment.vip', 'vaderpay.net', '2dlottery.com', 'fastmart168.com',
  'letspayfast.com', 'ui3cccomiximpro.com'
], 'VUE_APP_PAYMENT_HOST_ALLOWLIST')

export const EXTERNAL_HOST_ALLOWLIST = parseHostList([
  ...BRAND_HOST_DOMAINS,
  't.me', 'wa.me', 'whatsapp.com', 'telegram.org'
], 'VUE_APP_EXTERNAL_HOST_ALLOWLIST')

function parseHostList(baseList, envKey) {
  const fromEnv = (typeof process !== 'undefined' && process.env && envKey ? process.env[envKey] : '') || ''
  const extra = String(fromEnv).split(/[,\s]+/)
  return Array.from(new Set([...baseList, ...extra]
    .map(d => String(d == null ? '' : d).trim().toLowerCase().replace(/^\.+/, ''))
    .filter(Boolean)))
}

export function isAllowedHost(hostname, allowList) {
  const host = String(hostname == null ? '' : hostname).trim().toLowerCase().replace(/\.$/, '')
  if (!host) return false
  const list = safeArray(allowList)
  // 后缀匹配:host===d 或 host.endsWith('.'+d);禁 includes 防 evil-otgame.bet / otgame.bet.evil.com
  return list.some(d => host === d || host.endsWith('.' + d))
}

export function hostnameOfUrl(href) {
  try {
    return new URL(href).hostname
  } catch (e) {
    return ''
  }
}

export function reportDisallowedHost(context, href, allowList) {
  const host = hostnameOfUrl(href)
  if (!host || isAllowedHost(host, allowList)) return
  try {
    // eslint-disable-next-line no-console
    console.warn('[host-allowlist][log-only] off-allowlist host', { context, host, href })
  } catch (e) { /* log-only:console 不可用时静默 */ }
  try {
    const win = getBrowserWindow()
    if (win && win.navigator && typeof win.navigator.sendBeacon === 'function') {
      win.navigator.sendBeacon('/a/sec/host-allowlist-miss', JSON.stringify({ context, host, href, ts: Date.now() }))
    }
  } catch (e) { /* log-only:sendBeacon 不可用时静默 */ }
}

export function safeExternalUrl(value) {
  const rawUrl = safeString(value).trim()
  if (!rawUrl) return ''
  if (/^[a-z][a-z0-9+.-]*:/i.test(rawUrl) && !/^https?:\/\//i.test(rawUrl)) return ''
  const candidate = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl.replace(/^\/+/, '')}`
  try {
    const url = new URL(candidate)
    if (!['http:', 'https:'].includes(url.protocol) || !url.hostname) return ''
    reportDisallowedHost('safeExternalUrl', url.href, EXTERNAL_HOST_ALLOWLIST) // log-only:仅观测,不拦截
    return url.href
  } catch (e) {
    return ''
  }
}

function getBrowserWindow() {
  try {
    if (typeof window === 'undefined') return null
    return window
  } catch (e) {
    return null
  }
}

function getBrowserLocation(win = getBrowserWindow()) {
  try {
    if (!win || !win.location) return null
    return win.location
  } catch (e) {
    return null
  }
}

export function openExternalUrl(value, target = '_blank') {
  const url = safeExternalUrl(value)
  if (!url) return false
  const win = getBrowserWindow()
  if (!win || typeof win.open !== 'function') return false
  try {
    win.open(url, target, 'noopener,noreferrer')
    return true
  } catch (e) {
    return false
  }
}

export function openCurrentUrl(value) {
  const url = safeExternalUrl(value)
  if (!url) return false
  const win = getBrowserWindow()
  const location = getBrowserLocation(win)
  if (!location) return false
  try {
    location.href = url
    return true
  } catch (e) {
    return false
  }
}

export function sortByOrderDesc(list, orderKey = 'ordered') {
  return safeArray(list)
    .slice()
    .sort((a, b) => safeNumber(safeObject(b)[orderKey]) - safeNumber(safeObject(a)[orderKey]))
}

export function showLogin(vm) {
  if (!vm) return
  if (vm.$bus) {
    vm.$bus.$emit('openLogin')
    return
  }
  vm.showPopup = true
}
